// The project screen's panels for every format but the ad (spec-v3-formats.md, Web E1): the Brief (driven by the
// format's fields, with the look switcher), Sources (documentary, explainer, podcast, demo), the Cast (moved to
// components/Cast.tsx in v4), the Song (music video) and the Photos (slideshow, demo).
// Every change goes through the same endpoints the Director's tools use, and tells the Director what was tapped.
import { useEffect, useState, type FormEvent } from "react";
import { ASPECT_WORD, fmtLen, missingFields, panelsFor, TITLE_BACKGROUNDS, TITLE_FONTS, TITLE_MOTIONS, videoBrief, type BriefField, type FormatInfo, type TitleCardBrief, type CaptionChoice, type TransitionChoice, type TransitionSpeed, type VideoBriefDraft } from "../formats";
import type { Aspect, Project } from "../types";
import { Icon, Sheet, useLoad } from "./bits";
import { api } from "../api";
import { SunoPack, SunoPackBody } from "./SunoPack";
import { AspectPicker, BriefInput, fieldLabel, LengthChips, LookSwatches, PhotoPicker, SongPicker } from "./BriefFields";
import { CastPanel } from "./Cast";
import { Fold } from "./Fold"; // v4 (V4-P2): the look, sources and photos fold away under the brief
import { firstLine } from "../foldtext";
import { tapTool } from "../actions";

export type SaveBrief = (patch: VideoBriefDraft, said: string) => Promise<void>;

/** Fields that get their own panel (not a cell in the Brief). */
const OWN_PANEL = new Set(["sources", "characters", "songAssetId", "photoAssetIds", "look", "durationS", "aspect"]);

// ------------------------------------------------------------------ brief

export function VideoBriefPanel({ p, format, save }: { p: Project; format: FormatInfo; save: SaveBrief }) {
  const b = videoBrief(p);
  const [editing, setEditing] = useState<BriefField | null>(null);
  const missing = missingFields(format, b);
  const cells = format.briefFields.filter((f) => !OWN_PANEL.has(f.key));
  return (
    <section className="slate vbrief" aria-labelledby="brief-h">
      <header className="slate-head">
        <h2 id="brief-h">Brief</h2>
        <span className="slate-note">{missing.length ? `${missing.length} to fill in` : "Complete"}</span>
      </header>
      <dl className="slate-grid">
        {cells.map((f) => {
          const v = (b as Record<string, unknown>)[f.key];
          const text = typeof v === "string" ? v : "";
          return (
            <button type="button" key={f.key} className={`slate-cell slate-cell-wide${text ? "" : " is-empty"}`} onClick={() => setEditing(f)}>
              <dt>
                {fieldLabel(f)}
                {f.required && !text && <span className="vb-need"> needed</span>}
              </dt>
              <dd className={f.key === "idea" ? "vb-idea" : ""}>{text ? (f.input === "choice" ? text.replace(/^./, (c) => c.toUpperCase()) : text) : f.placeholder || (f.input === "choice" ? f.choices?.join(", ") : "Tap to add")}</dd>
            </button>
          );
        })}
        {format.durations.length > 1 && (
          <div className="slate-cell slate-cell-wide vb-row">
            <dt>Length</dt>
            <dd>
              <LengthChips durations={format.durations} value={b.durationS} onChange={(d) => void save({ durationS: d }, `set the length to ${fmtLen(d)}`)} />
            </dd>
          </div>
        )}
        {format.aspects.length > 1 && (
          <div className="slate-cell slate-cell-wide vb-row">
            <dt>Shape</dt>
            <dd>
              <AspectPicker aspects={format.aspects} value={b.aspect} onChange={(a: Aspect) => void save({ aspect: a }, `set the shape to ${ASPECT_WORD[a].toLowerCase()} ${a}`)} />
            </dd>
          </div>
        )}
      </dl>
      <LookSwitcher p={p} format={format} save={save} />
      {format.titleCard ? <TitleCardPanel p={p} save={save} /> : <ScriptTitlePanel p={p} />}
      <ClosingPanel p={p} />
      <TransitionsPanel p={p} save={save} />
      <CaptionsPanel p={p} format={format} save={save} />
      <LetterboxPanel p={p} format={format} save={save} />
      {editing && <FieldSheet field={editing} format={format} brief={b} onClose={() => setEditing(null)} onSave={(patch) => save(patch, `changed the brief's ${fieldLabel(editing).toLowerCase()}`)} />}
    </section>
  );
}

function FieldSheet({ field, format, brief, onClose, onSave }: { field: BriefField; format: FormatInfo; brief: VideoBriefDraft; onClose: () => void; onSave: (patch: VideoBriefDraft) => Promise<void> }) {
  const [draft, setDraft] = useState<VideoBriefDraft>(brief);
  return (
    <Sheet title={fieldLabel(field)} onClose={onClose}>
      <form
        className="sheet-body"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          const v = (draft as Record<string, unknown>)[field.key];
          void onSave({ [field.key]: typeof v === "string" ? v.trim() : v });
          onClose();
        }}
      >
        <BriefInput field={field} format={format} brief={draft} onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))} idPrefix="edit" />
        <button data-action="brief.save" type="submit" className="btn btn-primary btn-block">
          Save
        </button>
      </form>
    </Sheet>
  );
}

const FONT_WORD: Record<string, string> = { serif: "Elegant serif", classic: "Carved capitals", book: "Storybook", hand: "Handwritten", bold: "Heavy capitals", condensed: "Tall poster", round: "Friendly round", clean: "Clean modern" };
const BG_WORD: Record<string, string> = { canvas: "The look's own", color: "A colour", gradient: "A gradient", picture: "The next scene's picture" };
const MOTION_WORD: Record<string, string> = { fade: "Fades in", rise: "Rises in", zoom: "Zooms in", glow: "Neon glow", wipe: "Wipes in" };

/** The title: none, a card (at the start or after a scene) or drawn over a scene, and how it looks. Free; the next
 *  render shows it. The same choices as the Director's set_title_card. */
export function TitleCardPanel({ p, save }: { p: Project; save: SaveBrief }) {
  const t: TitleCardBrief = videoBrief(p).titleCard ?? {};
  const n = p.script?.scenes.length ?? 0;
  const place = t.place ?? "start";
  const value = place === "after" || place === "over" ? `${place}:${t.scene ?? 1}` : place;
  const set = (patch: Partial<TitleCardBrief>, said: string) => void save({ titleCard: { ...t, ...patch } }, said);
  const pick = (v: string) => {
    if (v === value) return;
    const [where, k] = v.split(":");
    if (where === "off" || where === "start") {
      const { scene: _s, atS: _a, ...rest } = t;
      void save({ titleCard: { ...rest, place: where } }, where === "off" ? "turned the title off" : "put the title card at the start");
    } else if (where === "after") set({ place: "after", scene: Number(k), atS: undefined }, `put the title card after scene ${k}`);
    else set({ place: "over", scene: Number(k), atS: t.atS ?? 1 }, `put the title over scene ${k}`);
  };
  const opts: [string, string][] = [["start", "A card at the start"], ...Array.from({ length: Math.max(0, n - 1) }, (_, i): [string, string] => [`after:${i + 1}`, `A card after scene ${i + 1}`]), ...Array.from({ length: n }, (_, i): [string, string] => [`over:${i + 1}`, `Over scene ${i + 1}`]), ["off", "No title"]];
  if (!opts.some(([v]) => v === value)) opts.unshift([value, value.replace(":", " scene ")]);
  const summary = place === "off" ? "None" : [t.font ? FONT_WORD[t.font] : "Plain", t.motion ? MOTION_WORD[t.motion]?.toLowerCase() : null].filter(Boolean).join(", ");
  return (
    <Fold id="brief.title" title="Title" summary={summary} className="vpanel vpanel-fold">
      <div className="field-row">
        <label className="field-label">Where</label>
        <select data-action="brief.titlecard" className="field-select" value={value} onChange={(e) => pick(e.target.value)} aria-label="Where the title goes">
          {opts.map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </div>
      {place === "over" && (
        <div className="field-row">
          <label className="field-label">Comes in after (seconds)</label>
          <input data-action="brief.titlecard" className="field-input" type="number" min={0} max={60} step={0.5} key={`t${t.atS}`} defaultValue={t.atS ?? 1} onBlur={(e) => Number(e.target.value) !== (t.atS ?? 1) && set({ atS: Math.max(0, Math.min(60, Number(e.target.value) || 0)) }, `set the title to come in ${e.target.value} seconds into scene ${t.scene ?? 1}`)} />
        </div>
      )}
      {place !== "off" && (
        <>
          <div className="field-row">
            <label className="field-label">Lettering</label>
            <select data-action="brief.titlecard" className="field-select" value={t.font ?? ""} onChange={(e) => set({ font: (e.target.value || undefined) as TitleCardBrief["font"] }, `set the title's lettering to ${FONT_WORD[e.target.value] ?? "the look's own"}`)}>
              <option value="">The look's own</option>
              {TITLE_FONTS.map((f) => (
                <option key={f} value={f}>
                  {FONT_WORD[f]}
                </option>
              ))}
            </select>
          </div>
          <div className="field-row">
            <label className="field-label">Comes in</label>
            <select data-action="brief.titlecard" className="field-select" value={t.motion ?? ""} onChange={(e) => set({ motion: (e.target.value || undefined) as TitleCardBrief["motion"] }, `set the title's motion to ${MOTION_WORD[e.target.value] ?? "the look's own"}`)}>
              <option value="">The look's own</option>
              {TITLE_MOTIONS.map((m) => (
                <option key={m} value={m}>
                  {MOTION_WORD[m]}
                </option>
              ))}
            </select>
          </div>
          {place !== "over" && (
            <div className="field-row">
              <label className="field-label">Behind it</label>
              <select data-action="brief.titlecard" className="field-select" value={t.background ?? "canvas"} onChange={(e) => set({ background: e.target.value as TitleCardBrief["background"] }, `set the title's background to ${BG_WORD[e.target.value]?.toLowerCase()}`)}>
                {TITLE_BACKGROUNDS.map((b) => (
                  <option key={b} value={b}>
                    {BG_WORD[b]}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field-row">
            <label className="field-label">Colours</label>
            <input data-action="brief.titlecard" type="color" aria-label="Letters" key={`c${t.color}`} defaultValue={t.color ?? "#ffffff"} onBlur={(e) => e.target.value !== (t.color ?? "#ffffff") && set({ color: e.target.value }, `set the title's letters to ${e.target.value}`)} />
            <input data-action="brief.titlecard" type="color" aria-label="Accent" key={`a${t.accent}`} defaultValue={t.accent ?? "#e8a24a"} onBlur={(e) => e.target.value !== (t.accent ?? "#e8a24a") && set({ accent: e.target.value }, `set the title's accent to ${e.target.value}`)} />
            {place !== "over" && (t.background === "color" || t.background === "gradient") && (
              <input data-action="brief.titlecard" type="color" aria-label="Background" key={`b${t.bgColor}`} defaultValue={t.bgColor ?? "#101018"} onBlur={(e) => e.target.value !== (t.bgColor ?? "#101018") && set({ bgColor: e.target.value }, `set the title's background colour to ${e.target.value}`)} />
            )}
          </div>
          {t.description && <p className="field-hint">You asked for: {t.description}</p>}
          <p className="field-hint">Free. Shows in the next render. You can also just describe it to the Director.</p>
        </>
      )}
    </Fold>
  );
}

/** v4 (V4-P2): the look as a Fold ("brief.look"), open on the first visit until the script is written. */
export function LookSwitcher({ p, format, save }: { p: Project; format: FormatInfo; save: SaveBrief }) {
  const b = videoBrief(p);
  const current = format.looks.find((l) => l.id === b.look) ?? format.looks[0];
  if (format.looks.length < 2) return null;
  return (
    <Fold id="brief.look" title="Look" summary={current?.name ?? ""} defaultOpen={!p.script} className="vb-looks">
      {current?.description && <p className="muted vb-looks-head">{current.description}</p>}
      <LookSwatches format={format} value={b.look} onChange={(id) => id !== b.look && void save({ look: id }, `switched the look to ${format.looks.find((l) => l.id === id)?.name ?? id}`)} />
      {p.script && <p className="field-hint">New frames use the new look. Frames you've already made keep theirs until you redo them.</p>}
    </Fold>
  );
}

// ------------------------------------------------------------------ sources

type Finding = { sceneN: number; sentence: string; status: "sourced" | "drift" | "quote" | "unsourced"; detail: string };
const FACT_WORD: Record<Finding["status"], string> = { sourced: "In your sources", drift: "A number not in your sources", quote: "A quote not in your sources", unsourced: "Not in your sources" };

/** v4.1 B4 (2026-11-03): each factual line of the script checked against the sources, before it's voiced. Free. */
function FactCheck({ p }: { p: Project }) {
  const { data } = useLoad(() => api.factcheck(p.id).catch(() => null), [p.id, p.updatedAt]);
  if (!data) return null;
  const bad = data.findings.filter((f: Finding) => f.status !== "sourced");
  return (
    <div className={`factcheck${data.ok ? " is-ok" : ""}`} data-help="brief.factcheck">
      <p className="field-label">Fact check</p>
      <p className="factcheck-sum">
        <Icon name={data.ok ? "check" : "alert"} size={14} /> {data.summary}
      </p>
      {!!bad.length && (
        <ul className="factcheck-list">
          {bad.slice(0, 8).map((f: Finding, i: number) => (
            <li key={i}>
              <b>Scene {f.sceneN}</b> “{f.sentence}” <span className="muted">{FACT_WORD[f.status]}. {f.detail}</span>
            </li>
          ))}
        </ul>
      )}
      {!!bad.length && <p className="field-hint">Add the fact to your sources, or change the line (ask your Director to “fix the facts in scene {bad[0]!.sceneN}”).</p>}
    </div>
  );
}

export function SourcesPanel({ p, save }: { p: Project; save: SaveBrief }) {
  const b = videoBrief(p);
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(b.sources ?? "");
  useEffect(() => setText(b.sources ?? ""), [b.sources]);
  const cited = [...new Set((p.script?.scenes ?? []).map((s) => s.source).filter((s): s is string => !!s))];
  return (
    <Fold id="brief.sources" title="Sources" summary={b.sources ? firstLine(b.sources) : "None yet"} count={cited.length || undefined} defaultOpen={!b.sources} className="vpanel vpanel-fold">
      {b.sources ? <p className="src-text">{b.sources}</p> : <p className="muted">Paste the facts, notes, quotes and links this should stand on. The script only states facts from here, and shows where each one came from.</p>}
      {!!cited.length && (
        <div className="src-cited">
          <p className="field-label">On screen as source chips</p>
          <ul className="src-chips">
            {cited.map((c) => (
              <li key={c} className="source-chip source-chip-static">
                {c.replace(/^source:\s*/i, "")}
              </li>
            ))}
          </ul>
        </div>
      )}
      {b.sources && p.script && <FactCheck p={p} />}
      <button type="button" className="btn btn-quiet btn-small vpanel-act" onClick={() => setOpen(true)}>
        <Icon name="edit" size={14} /> {b.sources ? "Edit the sources" : "Add sources"}
      </button>
      {open && (
        <Sheet title="Sources" onClose={() => setOpen(false)}>
          <form
            className="sheet-body"
            onSubmit={(e) => {
              e.preventDefault();
              void save({ sources: text.trim() }, "updated the sources");
              setOpen(false);
            }}
          >
            <label className="field">
              <span className="field-label">Facts, notes and links</span>
              <textarea value={text} onChange={(e) => setText(e.target.value)} rows={10} placeholder={"Sept 9, 1947: operators of the Harvard Mark II found a moth in relay 70.\nhttps://…"} data-autofocus />
              <span className="field-hint">The next script (or rewrite) uses these. Nothing outside them is stated as fact.</span>
            </label>
            <button data-action="brief.save" type="submit" className="btn btn-primary btn-block">
              Save sources
            </button>
          </form>
        </Sheet>
      )}
    </Fold>
  );
}

// ------------------------------------------------------------------ cast (v4: components/Cast.tsx, V4-C4)

export { castOf } from "./Cast";
export { CastPanel };

// ------------------------------------------------------------------ song + photos

export function SongPanel({ p, save }: { p: Project; save: SaveBrief }) {
  const b = videoBrief(p);
  // v4 (V4-M1): "I'll make it in Suno": the pack's fold under the picker (the song, once uploaded, is picked here)
  const [chose, setChose] = useState(false);
  const suno = !b.songAssetId && (chose || p.suno?.status === "waiting");
  return (
    <section className="vpanel" aria-labelledby="song-h">
      <header className="vpanel-head">
        <h2 id="song-h">Song</h2>
      </header>
      <SongPicker
        value={b.songAssetId}
        suno={suno}
        onSuno={() => {
          setChose(true);
          if (b.songAssetId) void save({ songAssetId: undefined }, "chose to make the song in Suno");
        }}
        onChange={(id) => {
          setChose(false);
          void save({ songAssetId: id }, id ? "picked the song from their library" : "asked for a song to be made");
        }}
      />
      {(p.suno || chose) && <div className="vpanel-suno">{p.suno ? <SunoPack p={p} /> : <SunoPackBody p={p} />}</div>}
    </section>
  );
}

export function PhotosPanel({ p, save }: { p: Project; save: SaveBrief }) {
  const b = videoBrief(p);
  const [open, setOpen] = useState(false);
  const [ids, setIds] = useState<string[]>(b.photoAssetIds ?? []);
  const n = b.photoAssetIds?.length ?? 0;
  return (
    <Fold id="brief.photos" title="Photos" count={n || undefined} summary={n ? "In the order you picked them" : "None yet"} defaultOpen={!n && !p.script} className="vpanel vpanel-fold">
      <p className="muted">{n ? `${n} ${n === 1 ? "picture" : "pictures"}, shown in the order you picked them.` : "None yet. Without your own pictures, your Director paints them."}</p>
      <button type="button" className="btn btn-quiet btn-small vpanel-act" onClick={() => (setIds(b.photoAssetIds ?? []), setOpen(true))}>
        <Icon name="image" size={14} /> {n ? "Change the photos" : "Pick photos"}
      </button>
      {open && (
        <Sheet title="Photos" onClose={() => setOpen(false)}>
          <div className="sheet-body">
            <PhotoPicker value={ids} onChange={setIds} />
            <button
              data-action="brief.photos"
              type="button"
              className="btn btn-primary btn-block"
              onClick={() => {
                void save({ photoAssetIds: ids }, `picked ${ids.length} photos`);
                setOpen(false);
              }}
            >
              Use these {ids.length || ""}
            </button>
          </div>
        </Sheet>
      )}
    </Fold>
  );
}

/** Every panel a format shows beside the brief, in order. */
export function FormatPanels({ p, format, save, onProject, without = [] }: { p: Project; format: FormatInfo; save: SaveBrief; onProject: (p: Project) => void; without?: ("cast" | "brief")[] }) {
  const has = panelsFor(format);
  return (
    <>
      {!without.includes("brief") && <VideoBriefPanel p={p} format={format} save={save} />}
      {has.sources && <SourcesPanel p={p} save={save} />}
      {has.song && <SongPanel p={p} save={save} />}
      {has.photos && <PhotosPanel p={p} save={save} />}
      {has.cast && !without.includes("cast") && <CastPanel p={p} format={format} onProject={onProject} />}
    </>
  );
}

export const TRANSITION_WORD: Record<TransitionChoice, string> = { auto: "What suits the look", cut: "Cut (instant)", black: "Fade through black", white: "Fade through white", fade: "Dissolve", mixed: "The look's older mix" };
/** 2026-11-02 (Keaton's report: "there's no way to turn captions off"): subtitles on, off, or as the kind does them. */
export function CaptionsPanel({ p, format, save }: { p: Project; format: FormatInfo; save: SaveBrief }) {
  const c: CaptionChoice = videoBrief(p).captions ?? "auto";
  const kind = format.captions ?? "scene";
  const own = kind === "words" ? "subtitles" : kind === "scene" ? "on-screen captions" : "none";
  const word: Record<CaptionChoice, string> = { auto: `As this kind does them (${own})`, on: "On", off: "Off" };
  const summary = c === "off" ? "Off" : c === "on" ? "On" : kind === "none" ? "None" : kind === "words" ? "Subtitles" : "Captions";
  return (
    <Fold id="brief.captions" title="Subtitles" summary={summary} className="vpanel vpanel-fold">
      <div className="field-row">
        <label className="field-label" htmlFor="cap-mode">
          Subtitles and captions
        </label>
        <select id="cap-mode" data-action="brief.captions" className="field-select" value={c} onChange={(e) => void save({ captions: e.target.value as CaptionChoice }, `turned the captions ${e.target.value === "auto" ? "back to the default" : e.target.value}`)}>
          {(["auto", "on", "off"] as CaptionChoice[]).filter((k) => k !== "on" || kind === "none" || c === "on").map((k) => (
            <option key={k} value={k}>
              {word[k]}
            </option>
          ))}
        </select>
      </div>
      <p className="field-hint">Free; it shows in the next render.</p>
    </Fold>
  );
}

/** 2026-11-03 (Keaton: a vertical short film came out as a widescreen strip): the cinematic look's bars. Auto draws
 *  them only on a widescreen video, so a phone-shaped one fills the screen. Free; the next render shows it. */
export function LetterboxPanel({ p, format, save }: { p: Project; format: FormatInfo; save: SaveBrief }) {
  const b = videoBrief(p);
  const look = format.looks.find((l) => l.id === b.look) ?? format.looks[0];
  if (look?.render !== "cinematic") return null;
  const v: CaptionChoice = b.letterbox ?? "auto";
  const wide = (b.aspect ?? "16:9") === "16:9";
  const word: Record<CaptionChoice, string> = { auto: wide ? "On (widescreen)" : "Off (fills the screen)", on: "Always on", off: "Off" };
  return (
    <Fold id="brief.letterbox" title="Cinema bars" summary={v === "auto" ? (wide ? "On" : "Off") : v === "on" ? "On" : "Off"} className="vpanel vpanel-fold">
      <div className="field-row">
        <label className="field-label" htmlFor="lb-mode">
          Black bars above and below
        </label>
        <select id="lb-mode" data-action="brief.letterbox" className="field-select" value={v} onChange={(e) => void save({ letterbox: e.target.value as CaptionChoice }, `set the cinema bars to ${e.target.value}`)}>
          {(["auto", "on", "off"] as CaptionChoice[]).map((k) => (
            <option key={k} value={k}>
              {word[k]}
            </option>
          ))}
        </select>
      </div>
      <p className="field-hint">The bars make a widescreen film look like cinema. On a vertical or square video they shrink the picture to a strip, so they're off unless you turn them on. Free; it shows in the next render.</p>
    </Fold>
  );
}

/**
 * 2026-11-03 (Keaton's report): a short film or trailer keeps its title in the script (a title over the world shot, or
 * a card scene). The Director's set_title_card place=off takes it out of the opening; a rewrite won't put it back.
 */
export function ScriptTitlePanel({ p }: { p: Project }) {
  const b = videoBrief(p);
  const title = (b.title || p.script?.title || "").trim().toLowerCase();
  const scenes = p.script?.scenes ?? [];
  const half = Math.ceil(scenes.length / 2);
  const shows = scenes.flatMap((s, i) => {
    const v = (s as { visual?: { kind?: string; text?: string } }).visual;
    const t = (v?.kind === "title" && v.text?.trim().toLowerCase()) || "";
    return i < half && i < scenes.length - 1 && t && title && (t.includes(title) || title.includes(t)) ? [i + 1] : [];
  });
  const [busy, setBusy] = useState(false);
  const off = b.titleCard?.place === "off";
  if (!shows.length && !off) return null;
  return (
    <Fold id="brief.title" title="Title" summary={shows.length ? `In scene ${shows.join(", ")}` : "None"} className="vpanel vpanel-fold">
      {shows.length ? (
        <>
          <p className="field-hint">The title is part of the script: scene {shows.join(" and ")} shows it. Taking it out keeps the shot (a title card scene goes). Free; undoable.</p>
          <button
            type="button"
            data-action="brief.title-off"
            className="btn btn-quiet btn-small"
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              await tapTool("set_title_card", { project_id: p.id, place: "off" }, "took the title out of the opening");
              setBusy(false);
            }}
          >
            Take the title out of the opening
          </button>
        </>
      ) : (
        <p className="field-hint">No title at the opening; a rewrite won't add one. Undo the change to bring it back.</p>
      )}
    </Fold>
  );
}

/**
 * 2026-11-03 (Keaton's reports): the closing: the kind's own end card, none, or the closing words over a scene's
 * picture (no background) from when the voice says some words, to the end. Through set_title_card card=closing, so a
 * title already on that picture gives way to it, as by voice.
 */
export function ClosingPanel({ p }: { p: Project }) {
  const c = videoBrief(p).closing ?? {};
  const place = c.place ?? "auto";
  const n = p.script?.scenes.length ?? 0;
  const [busy, setBusy] = useState(false);
  const set = async (args: Record<string, unknown>, said: string) => {
    setBusy(true);
    await tapTool("set_title_card", { project_id: p.id, card: "closing", ...args }, said);
    setBusy(false);
  };
  const word: Record<string, string> = { auto: "The usual end card", off: "None: end on the last shot", over: "Words over the last shot" };
  return (
    <Fold id="brief.closing" title="Closing" summary={place === "over" ? (c.words ? `Over scene ${c.scene ?? n}, at "${c.words}"` : `Over scene ${c.scene ?? n}`) : place === "off" ? "None" : "The usual"} className="vpanel vpanel-fold">
      <div className="field-row">
        <label className="field-label" htmlFor="closing-place">
          How it ends
        </label>
        <select id="closing-place" data-action="brief.closing" className="field-select" value={place} disabled={busy} onChange={(e) => void set({ place: e.target.value }, `set the closing to ${word[e.target.value]?.toLowerCase()}`)}>
          {(["auto", "off", "over"] as const).map((k) => (
            <option key={k} value={k}>
              {word[k]}
            </option>
          ))}
        </select>
      </div>
      {place === "over" && (
        <>
          <div className="field-row">
            <label className="field-label" htmlFor="closing-scene">
              Over scene
            </label>
            <select id="closing-scene" data-action="brief.closing" className="field-select" value={String(c.scene ?? "")} disabled={busy} onChange={(e) => void set({ scene: Number(e.target.value) }, `put the closing over scene ${e.target.value}`)}>
              {!c.scene && <option value="">The last shot</option>}
              {Array.from({ length: n }, (_, i) => (
                <option key={i} value={String(i + 1)}>
                  Scene {i + 1}
                </option>
              ))}
            </select>
          </div>
          <div className="field-row">
            <label className="field-label" htmlFor="closing-words">
              Comes in when the voice says
            </label>
            <input id="closing-words" data-action="brief.closing" className="field-input" key={`w${c.words ?? ""}`} defaultValue={c.words ?? ""} placeholder="Join CAO Studio" disabled={busy} onBlur={(e) => e.target.value.trim() !== (c.words ?? "") && e.target.value.trim() && void set({ words: e.target.value.trim() }, `set the closing to come in on "${e.target.value.trim()}"`)} />
          </div>
          <div className="field-row">
            <label className="field-label" htmlFor="closing-text">
              Words shown
            </label>
            <input id="closing-text" data-action="brief.closing" className="field-input" key={`t${c.text ?? ""}`} defaultValue={c.text ?? ""} placeholder="The usual card's words" maxLength={80} disabled={busy} onBlur={(e) => e.target.value.trim() !== (c.text ?? "") && void set({ text: e.target.value.trim() }, `set the closing words to "${e.target.value.trim()}"`)} />
          </div>
        </>
      )}
      <p className="field-hint">Over the last shot: no card of its own and no background, from that moment to the end. Free; it shows in the next render.</p>
    </Fold>
  );
}

const SPEED_WORD: Record<TransitionSpeed, string> = { quick: "Quick", normal: "Normal", slow: "Slow" };

/** 2026-11-02: how scenes change, for the whole video (the Director's set_transitions; a single scene's change comes
 *  from a note or the Director). Free; the next render shows it. */
export function TransitionsPanel({ p, save }: { p: Project; save: SaveBrief }) {
  const b = videoBrief(p);
  const t: TransitionChoice = b.transition ?? "mixed";
  const speed: TransitionSpeed = b.transitionSpeed ?? (t === "mixed" ? "normal" : "quick");
  const speedMatters = t !== "cut";
  return (
    <Fold id="brief.transitions" title="Transitions" summary={`${TRANSITION_WORD[t]}${speedMatters && t !== "mixed" ? `, ${SPEED_WORD[speed].toLowerCase()}` : ""}`} className="vpanel vpanel-fold">
      <div className="field-row">
        <label className="field-label" htmlFor="tr-style">
          Between scenes
        </label>
        <select id="tr-style" data-action="brief.transitions" className="field-select" value={t} onChange={(e) => void save({ transition: e.target.value as TransitionChoice }, `set the transitions to ${TRANSITION_WORD[e.target.value as TransitionChoice].toLowerCase()}`)}>
          {(Object.keys(TRANSITION_WORD) as TransitionChoice[]).map((k) => (
            <option key={k} value={k}>
              {TRANSITION_WORD[k]}
            </option>
          ))}
        </select>
      </div>
      {speedMatters && (
        <div className="field-row">
          <label className="field-label" htmlFor="tr-speed">
            Speed
          </label>
          <select id="tr-speed" data-action="brief.transitions" className="field-select" value={speed} onChange={(e) => void save({ transitionSpeed: e.target.value as TransitionSpeed }, `made the transitions ${e.target.value}`)}>
            {(Object.keys(SPEED_WORD) as TransitionSpeed[]).map((k) => (
              <option key={k} value={k}>
                {SPEED_WORD[k]}
              </option>
            ))}
          </select>
        </div>
      )}
      <p className="field-hint">Free; it shows in the next render. Ask your Director to change just one scene's transition.</p>
    </Fold>
  );
}
